// src/Components/Drawer/CustomDrawer/DrawerSidebar.js

import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';

import {
  MenuDocsColor,
  MenuDocsGrey,
  MenuGeneralInfoColor,
  MenuGeneralInfoGrey,
  MARKER_NEW_SVG_STRING,
  ClipsIcon,
  MenuPolygonColor,
  MenuLineColor,
} from '../../../assets/icons/CustomIcons';

import { SIDEBAR_WIDTH } from './DrawerUtils';

const NEW_ICON_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(MARKER_NEW_SVG_STRING)}`;

const VisHoverIcon = (props) => (
  <SvgIcon {...props} viewBox='0 0 100 100'>
    <circle cx='50' cy='50' r='45' fill='currentColor' />
    <g transform='translate(2, -2)'>
      <path
        d='M 68.3 45.1 C 66.3 35.1 57.5 27.6 47 27.6 C 38.6 27.6 31.3 32.4 27.7 39.3 C 19 40.3 12.2 47.6 12.2 56.6 C 12.2 66.2 20 74 29.6 74 L 67.2 74 C 75.2 74 81.8 67.5 81.8 59.5 C 81.8 51.9 76.1 45.6 68.3 45.1 Z'
        fill='white'
      />
    </g>
  </SvgIcon>
);

export default function DrawerSidebar({
  activeMenu,
  activeTab,
  setActiveTab,
  handleTabSwitch,
  selectedMarker,
  tempData,
  draftSiblings,
  sidebarTitle,
  tabsEnabled,
}) {
  const t = (selectedMarker?.type || '').toLowerCase();
  const tTemp = (tempData?.type || '').toLowerCase();

  const markerExists =
    selectedMarker?.groupHasMarker ||
    tempData?.groupHasMarker ||
    t === 'marker' ||
    tTemp === 'marker' ||
    !!draftSiblings?.marker;
  const polyExists =
    selectedMarker?.groupHasPolygon ||
    tempData?.groupHasPolygon ||
    t === 'polygon' ||
    tTemp === 'polygon' ||
    !!draftSiblings?.polygon;
  const lineExists =
    selectedMarker?.groupHasPolyline ||
    tempData?.groupHasPolyline ||
    t === 'line' ||
    t === 'polyline' ||
    tTemp === 'line' ||
    tTemp === 'polyline' ||
    !!draftSiblings?.line;

  const isMarkerTab = activeTab === 'MARKERS';
  const isPolyTab = activeTab === 'POLYGON';
  const isLineTab = activeTab === 'POLYLINE';

  const hasMarkerLock = markerExists && !isMarkerTab;
  const hasPolygonLock = polyExists && !isPolyTab;
  const hasPolylineLock = lineExists && !isLineTab;

  const GroupDivider = () => (
    <Box sx={{ width: '60%', borderTop: '2px solid #fff', my: 0.5 }} />
  );

  const stopProp = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  return (
    <Box
      onClick={stopProp}
      onMouseDown={stopProp}
      onWheel={stopProp}
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        zIndex: 1400,
        pointerEvents: 'auto',
        overflowX: 'hidden',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        flexShrink: 0,
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 1 }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <GroupDivider />

          {/* --- MARKERS ICON --- */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'visible',
              zIndex: 3,
            }}>
            <IconButton
              title='Marker'
              sx={{ p: 0, width: '100%', display: 'flex', justifyContent: 'center' }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleTabSwitch('MARKERS', 'MARKER');
              }}>
              <img
                src={NEW_ICON_SVG}
                alt='Markers'
                style={{
                  width: 35,
                  height: 35,
                  filter: isMarkerTab ? 'none' : 'grayscale(100%)',
                  opacity: isMarkerTab ? 1 : markerExists ? 0.8 : 0.6,
                  transition: 'all 0.2s ease',
                }}
              />
            </IconButton>
          </Box>

          <GroupDivider />

          {/* --- POLYGON ICON --- */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'visible',
              zIndex: 3,
            }}>
            <IconButton
              title='Polygon'
              sx={{
                p: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mt: '-13px', // AMENDED: Reduced by an additional 3px
                mb: '-20px',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleTabSwitch('POLYGON', 'POLYGON');
              }}>
              <MenuPolygonColor
                sx={{
                  fontSize: 65,
                  filter: isPolyTab ? 'none' : 'grayscale(100%)',
                  opacity: isPolyTab ? 1 : polyExists ? 0.8 : 0.6,
                  transition: 'all 0.2s ease',
                }}
              />
            </IconButton>
          </Box>

          <GroupDivider />

          {/* --- POLYLINE ICON --- */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'visible',
              zIndex: 3,
            }}>
            <IconButton
              title='Polyline'
              sx={{
                p: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mt: '-13px', // AMENDED: Reduced by an additional 3px
                mb: '-20px',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleTabSwitch('POLYLINE', 'LINE');
              }}>
              <MenuLineColor
                sx={{
                  fontSize: 65,
                  filter: isLineTab ? 'none' : 'grayscale(100%)',
                  opacity: isLineTab ? 1 : lineExists ? 0.8 : 0.6,
                  transition: 'all 0.2s ease',
                }}
              />
            </IconButton>
          </Box>

          <GroupDivider />

          <Typography
            sx={{
              color: ['MARKERS', 'POLYGON', 'POLYLINE'].includes(activeTab)
                ? '#fff'
                : 'rgba(255,255,255,0.8)',
              opacity: hasMarkerLock && hasPolygonLock && hasPolylineLock ? 0.5 : 1,
              fontSize: '9px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              mt: 0.5,
            }}>
            Markers
          </Typography>
        </Box>

        {/* --- VIS/HOVER ICON --- */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            mt: '8px', // AMENDED: Reduced by an additional 3px
            mb: 0,
          }}>
          <IconButton
            title='Vis/Hover'
            sx={{ p: 0, width: '100%' }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setActiveTab('VIS_HOVER');
            }}>
            <VisHoverIcon
              sx={{
                fontSize: 48,
                color:
                  activeTab === 'VIS_HOVER' ? '#57cfff' : tabsEnabled ? '#666' : '#444',
                opacity: tabsEnabled ? 1 : 0.5,
              }}
            />
          </IconButton>
          <Typography
            sx={{
              color: activeTab === 'VIS_HOVER' ? '#fff' : 'rgba(255,255,255,0.8)',
              fontSize: '9px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              mt: 0.5,
              textAlign: 'center',
              lineHeight: 1.1,
            }}>
            Vis/Hover
          </Typography>
        </Box>

        {/* --- GENERAL INFO ICON --- */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            mt: '-8px', // AMENDED: Reduced by an additional 3px
          }}>
          <IconButton
            title='Info'
            sx={{
              p: 0,
              width: '100%',
              mt: '-10px',
              mb: '-39px',
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setActiveTab('INFO');
            }}>
            {activeTab === 'INFO' ? (
              <MenuGeneralInfoColor sx={{ fontSize: 110 }} />
            ) : (
              <MenuGeneralInfoGrey
                sx={{ fontSize: 110, opacity: tabsEnabled ? 1 : 0.5 }}
              />
            )}
          </IconButton>
          <Typography
            sx={{
              color: activeTab === 'INFO' ? '#fff' : 'rgba(255,255,255,0.8)',
              fontSize: '9px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              mt: 0.5,
              mb: 0.5,
              textAlign: 'center',
              lineHeight: 1.1,
            }}>
            Info
          </Typography>
        </Box>

        {/* --- CLIPS ICON --- */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'visible',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 2,
            mt: '8px', // AMENDED: Reduced by an additional 3px
          }}>
          <IconButton
            title='Photos & Clips'
            sx={{ p: 0, width: '100%' }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setActiveTab('PHOTOS');
            }}>
            <ClipsIcon
              sx={{
                fontSize: 43,
                filter: activeTab === 'PHOTOS' ? 'none' : 'grayscale(100%)',
                opacity: activeTab === 'PHOTOS' ? 1 : tabsEnabled ? 1 : 0.5,
              }}
            />
          </IconButton>
          <Typography
            sx={{
              color: activeTab === 'PHOTOS' ? '#fff' : 'rgba(255,255,255,0.8)',
              fontSize: '9px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              mt: 1,
              mb: 0.5,
              zIndex: 1,
              textAlign: 'center',
              lineHeight: 1.1,
            }}>
            Photos
            <br />
            Clips
          </Typography>
        </Box>

        {/* --- DOCS ICON --- */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            mt: '0px', // AMENDED: Reduced by an additional 3px
          }}>
          <IconButton
            title='Docs'
            sx={{
              p: 0,
              width: '100%',
              mt: '-10px',
              mb: '-35px',
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setActiveTab('DOCS');
            }}>
            {activeTab === 'DOCS' ? (
              <MenuDocsColor sx={{ fontSize: 110 }} />
            ) : (
              <MenuDocsGrey sx={{ fontSize: 110, opacity: tabsEnabled ? 1 : 0.5 }} />
            )}
          </IconButton>
          <Typography
            sx={{
              color: activeTab === 'DOCS' ? '#fff' : 'rgba(255,255,255,0.8)',
              fontSize: '9px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              mt: 0.5,
              mb: 0.5,
              textAlign: 'center',
              lineHeight: 1.1,
            }}>
            Docs
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
